import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, FieldLabel, Modal, showSnackbar, TextInput } from 'fluxo-ui';
import { BuildingIcon, PlusIcon } from 'fluxo-ui/icons';

import type { ComponentEvent } from 'fluxo-ui';

import { USER_ROLES } from '../common/constants';
import { Card, EmptyState, PageHeader } from '../components/common';
import organizationService from '../services/organization.service';
import { useAuthStore } from '../store/authStore';
import { formatDateTime } from '../utils/formatting';

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

const OrganizationsListPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', slug: '', emailDomain: '' });
    const [createErrors, setCreateErrors] = useState<{ name?: string; slug?: string }>({});
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (user?.role === USER_ROLES.SUPER_ADMIN) {
            fetchOrganizations();
        } else {
            navigate('/dashboard');
            showSnackbar('Unauthorized access', 'Error', { type: 'error' });
        }
    }, [user, navigate]);

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            const response = await organizationService.getAllOrganizations();
            setOrganizations(Array.isArray(response) ? response : response?.organizations || []);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to fetch organizations', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const resetCreateForm = () => {
        setCreateForm({ name: '', slug: '', emailDomain: '' });
        setCreateErrors({});
    };

    const handleCreate = async () => {
        const errors: { name?: string; slug?: string } = {};
        if (!createForm.name.trim()) errors.name = 'Name is required';
        if (!createForm.slug.trim()) errors.slug = 'Slug is required';
        if (Object.keys(errors).length > 0) {
            setCreateErrors(errors);
            return;
        }

        setCreating(true);
        try {
            await organizationService.createOrganization({
                name: createForm.name.trim(),
                slug: createForm.slug.trim(),
                emailDomain: createForm.emailDomain.trim() || undefined,
            });
            showSnackbar('Organization created successfully', 'Success', { type: 'success' });
            setShowCreateModal(false);
            resetCreateForm();
            await fetchOrganizations();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to create organization', 'Error', { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Organizations"
                description="Manage all organizations in the system"
                className="mb-0"
                actions={
                    <Button
                        label="Create Organization"
                        variant="primary"
                        leftIcon={PlusIcon}
                        onClick={() => {
                            resetCreateForm();
                            setShowCreateModal(true);
                        }}
                    />
                }
            />

            <Modal
                isOpen={showCreateModal}
                onClose={() => !creating && setShowCreateModal(false)}
                title="Create Organization"
                size="md"
            >
                <div className="space-y-4">
                    <FieldLabel label="Name" required error={createErrors.name}>
                        <TextInput
                            value={createForm.name}
                            onChange={(e: ComponentEvent<string>) => {
                                const shouldSyncSlug = createForm.slug === slugify(createForm.name);
                                setCreateForm({
                                    ...createForm,
                                    name: e.value,
                                    slug: shouldSyncSlug ? slugify(e.value) : createForm.slug,
                                });
                                if (createErrors.name) setCreateErrors({ ...createErrors, name: undefined });
                            }}
                            placeholder="Acme Corporation"
                            disabled={creating}
                        />
                    </FieldLabel>

                    <FieldLabel label="Slug" required error={createErrors.slug} hint="Unique identifier used in URLs">
                        <TextInput
                            value={createForm.slug}
                            onChange={(e: ComponentEvent<string>) => {
                                setCreateForm({ ...createForm, slug: slugify(e.value) });
                                if (createErrors.slug) setCreateErrors({ ...createErrors, slug: undefined });
                            }}
                            placeholder="acme-corporation"
                            disabled={creating}
                        />
                    </FieldLabel>

                    <FieldLabel label="Email Domain" hint="Optional — used for domain-based join requests">
                        <TextInput
                            value={createForm.emailDomain}
                            onChange={(e: ComponentEvent<string>) => setCreateForm({ ...createForm, emailDomain: e.value })}
                            placeholder="acme.com"
                            disabled={creating}
                        />
                    </FieldLabel>

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            onClick={() => setShowCreateModal(false)}
                            disabled={creating}
                        />
                        <Button label="Create" variant="primary" onClick={handleCreate} isLoading={creating} disabled={creating} />
                    </div>
                </div>
            </Modal>

            {/* Organizations List */}
            <Card padding="none" className="overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading...</div>
                ) : organizations.length === 0 ? (
                    <EmptyState
                        icon={BuildingIcon}
                        title="No organizations found"
                        description="Organizations you create will appear here."
                    />
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {organizations.map((org: any) => (
                            <div
                                key={org._id}
                                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer"
                                onClick={() => navigate(`/organizations/${org._id}`)}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-sm font-semibold flex items-center justify-center">
                                            {(org.name || '?').trim().substring(0, 2).toUpperCase()}
                                        </span>
                                        <div className="min-w-0">
                                            <h3 className="font-medium text-slate-900 dark:text-white truncate">{org.name}</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                                                {org.domain || 'No domain'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                        <span className="hidden sm:inline text-sm text-slate-500 dark:text-slate-400">
                                            {formatDateTime(org.createdAt)}
                                        </span>
                                        <Button
                                            label="View"
                                            size="sm"
                                            variant="default"
                                            layout="outlined"
                                            onClick={() => navigate(`/organizations/${org._id}`)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default OrganizationsListPage;
